import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import logo from "../../assets/images/ecom_tamil_logo.png";
import { ShieldCheck, ArrowLeft, ArrowRight, RotateCw, Mail, KeyRound, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import { postRequest } from "../../utils/networking";
import { apiUrls } from "../../utils/url";
import CommonToastContainer from "../../components/CommonToastContainer";

export default function VerifyOtp() {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email || "abina.abi1127@gmail.com";

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(30);
  const inputRefs = useRef([]);

  const showToast = (message, type = "success") => {
    if (type === "success") toast.success(message);
    else if (type === "error") toast.error(message);
    else if (type === "info") toast.info(message);
    else if (type === "warning") toast.warning(message);
  };

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  useEffect(() => {
    let timer;
    if (resendCountdown > 0) {
      timer = setInterval(() => {
        setResendCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCountdown]);

  const handleChange = (index, value) => {
    if (value && !/^\d+$/.test(value)) return;

    const newOtp = [...otp];
    if (value.length > 1) {
      const pastedDigits = value.slice(0, 6).split("");
      pastedDigits.forEach((digit, i) => {
        if (i < 6) newOtp[i] = digit;
      });
      setOtp(newOtp);
      const nextFocus = Math.min(pastedDigits.length, 5);
      if (inputRefs.current[nextFocus]) {
        inputRefs.current[nextFocus].focus();
      }
      return;
    }

    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0 && inputRefs.current[index - 1]) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    if (!/^\d+$/.test(pastedData)) return;

    const digits = pastedData.slice(0, 6).split("");
    const newOtp = [...otp];
    digits.forEach((digit, i) => {
      newOtp[i] = digit;
    });
    setOtp(newOtp);
    const focusIndex = Math.min(digits.length, 5);
    if (inputRefs.current[focusIndex]) {
      inputRefs.current[focusIndex].focus();
    }
  };

  const handleResendOtp = async () => {
    if (resendCountdown > 0 || isResending) return;
    setIsResending(true);

    try {
      const data = await postRequest(apiUrls.generateotp, { email: email });
      console.log("Resend OTP Response:", data);
      if (data.status === "S" || data.success) {
        showToast(data.message || "OTP resent successfully!", "success");
        setResendCountdown(30);
      } else {
        showToast(data.message || "Failed to resend OTP", "error");
      }
    } catch (error) {
      console.error("Resend OTP Error:", error);
      showToast(error.message || "Failed to resend OTP. Please ensure the backend server is running.", "error");
    } finally {
      setIsResending(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const enteredOtp = otp.join("");
    if (enteredOtp.length < 6) {
      showToast("Please enter the complete 6-digit OTP", "warning");
      return;
    }

    setIsLoading(true);
    const targetEmail = email || "abina.abi1127@gmail.com";
    const targetUserId = Number(location.state?.userid || 270);

    try {
      const data = await postRequest(apiUrls.verifyotp, {
        userid: targetUserId,
        email: targetEmail,
        otp: enteredOtp
      });
      console.log("Verify OTP Response:", data);

      if (data.status === "S" || data.success) {
        showToast(data.message || "OTP verified successfully!", "success");
        navigate("/reset-password", {
          state: {
            email: targetEmail,
            userid: targetUserId,
            otp: enteredOtp
          }
        });
      } else {
        showToast(data.message || "Invalid OTP. Please try again.", "error");
      }
    } catch (error) {
      console.error("Verify OTP Error:", error);
      showToast(error.message || "Failed to verify OTP. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-[#FFFDF9] relative overflow-hidden flex flex-col items-center justify-center p-4 sm:p-6 py-10 selection:bg-orange-100 selection:text-orange-900">
        {/* Soft Background Warm Blur Elements */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none -z-10">
          <div className="absolute top-0 right-0 w-[450px] h-[450px] bg-orange-200/30 rounded-full blur-[100px] -translate-y-1/3 translate-x-1/4" />
          <div className="absolute top-1/3 left-0 w-[400px] h-[400px] bg-amber-100/40 rounded-full blur-[90px] -translate-x-1/3" />
          <div className="absolute bottom-0 right-1/4 w-[450px] h-[450px] bg-orange-100/35 rounded-full blur-[100px] translate-y-1/4" />
        </div>

        {/* Main Card */}
        <div className="w-full max-w-[440px] bg-white rounded-3xl border border-orange-100/70 shadow-[0_12px_45px_-12px_rgba(234,88,12,0.12)] overflow-hidden transition-all duration-300">
          
          {/* Card Header */}
          <div className="p-6 pb-2 sm:p-7 sm:pb-3 flex items-center justify-between">
            <div 
              onClick={() => navigate("/")} 
              className="flex items-center gap-2 cursor-pointer group"
            >
              <img
                src={logo}
                alt="Ecom Tamil"
                className="h-10 w-auto object-contain transition-transform group-hover:scale-105"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = "none";
                }}
              />
            </div>

            <Link
              to="/login"
              state={{ email }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 hover:bg-slate-100 border border-slate-200/80 text-[11px] font-medium text-slate-600 transition active:scale-95 cursor-pointer"
            >
              <ArrowLeft size={12} />
              <span>Back to Login</span>
            </Link>
          </div>

          {/* Form Content Area */}
          <div className="p-6 pt-3 sm:p-7 sm:pt-4">
            
            {/* Heading Section */}
            <div className="mb-6">
              <div className="inline-flex items-center gap-1.5 text-xs font-bold text-orange-600 mb-1.5">
                <ShieldCheck size={16} className="text-orange-500" />
                <span>Verification Code</span>
              </div>
              <h1 className="text-2xl sm:text-[26px] font-black text-slate-800 tracking-tight leading-tight mb-1.5">
                Enter OTP
              </h1>
              <p className="text-xs text-slate-500 leading-relaxed">
                We've sent a 6-digit verification code to
              </p>
              <div className="inline-flex items-center gap-1.5 mt-1.5 px-2.5 py-1 bg-orange-50/80 border border-orange-100 rounded-lg text-xs font-medium text-orange-700 max-w-full">
                <Mail size={12} className="shrink-0" />
                <span className="truncate">{email}</span>
              </div>
            </div>

            {/* OTP Input Form */}
            <form onSubmit={handleVerifyOtp} className="space-y-5">
              
              <div className="flex items-center justify-between gap-2 sm:gap-2.5">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    disabled={isLoading}
                    className="w-11 h-12 sm:w-12 sm:h-14 text-center text-lg sm:text-xl font-bold bg-white border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all shadow-sm"
                  />
                ))}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading || otp.join("").length < 6}
                className={`w-full py-3 px-4 rounded-xl font-bold text-xs sm:text-sm transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer ${
                  isLoading || otp.join("").length < 6
                    ? "bg-slate-200 text-slate-400 cursor-not-allowed shadow-none"
                    : "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-md hover:shadow-lg active:scale-[0.99]"
                }`}
              >
                {isLoading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>Verifying...</span>
                  </>
                ) : (
                  <>
                    <span>Verify OTP</span>
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </form>

            {/* Resend Section */}
            <div className="mt-6 pt-5 border-t border-slate-100 flex flex-col items-center gap-2 text-center text-xs">
              <p className="text-slate-500">
                Didn't receive the code?
              </p>
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={resendCountdown > 0 || isResending}
                className={`inline-flex items-center gap-1.5 font-medium transition cursor-pointer ${
                  resendCountdown > 0 || isResending
                    ? "text-slate-400 cursor-not-allowed"
                    : "text-orange-600 hover:text-orange-700 hover:underline"
                }`}
              >
                <RotateCw size={13} className={isResending ? "animate-spin" : ""} />
                <span>
                  {resendCountdown > 0 
                    ? `Resend OTP in ${resendCountdown}s` 
                    : isResending ? "Sending..." : "Resend OTP"}
                </span>
              </button>
            </div>

          </div>

          {/* Footer note */}
          <div className="px-6 py-3.5 bg-slate-50/70 border-t border-slate-100/80 text-center">
            <p className="text-[11px] text-slate-400 flex items-center justify-center gap-1.5">
              <KeyRound size={12} className="text-orange-500" />
              <span>Secure 256-bit encrypted verification</span>
            </p>
          </div>

        </div>
      </div>
      <CommonToastContainer />
    </>
  );
}
